import React, { useCallback, useEffect, useState } from 'react';
import { number, object, ref, string } from 'yup';
import styled from 'styled-components';
import { FormikProvider, useFormik } from 'formik';
import { type DialogV2ContentValue } from './types';
import { PAGE_TEXT } from '../lang';
import {
  type GPAConvertRule,
  useConvertGPAV2,
  useGetGPAConvertRule,
} from '../../../graphql';
import {
  Dialog,
  Select,
  type SelectOption,
  TextField,
  Button,
} from '../../../../../components';

const {
  ORIGINAL_GPA,
  CONVERTED_GPA,
  CONVERT_RULE,
  SELECTED_RULE,
  NOTE,
  RULE_DETAIL,
  RECORD,
} = PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.GPA_DIALOG;
const NA = 'Not Applicable';
export const GPAConversionDialogV2: React.FC<{
  dialogTitle: string;
  openDialog: boolean;
  institutionName: string;
  countryRegion: string;
  viewMode: boolean;
  officialMark: number;
  outOfGpa: number;
  convertGPARuleId?: string;
  convertedMark?: number;
  onDialogClose: (dialogContent: DialogV2ContentValue) => void;
  onDialogApply?: (dialogContent: DialogV2ContentValue) => void;
}> = props => {
  const {
    dialogTitle,
    openDialog,
    institutionName,
    countryRegion,
    officialMark,
    convertGPARuleId,
    convertedMark,
    outOfGpa,
    viewMode,
    onDialogClose,
    onDialogApply,
  } = props;
  const [openGPA, setOpenGPA] = useState(openDialog);
  const [matchedRules, setMatchedRules] = useState<GPAConvertRule[]>([]);
  const chosenRuleLabel =
    matchedRules.find(rule => rule._id === convertGPARuleId)?.ruleName ?? 'N/A';
  const enableToApply = onDialogApply !== undefined;
  useEffect(() => {
    setOpenGPA(openDialog);
  }, [openDialog]);

  const getGPAConvertRule = useGetGPAConvertRule();
  const getMatchedRule = async (
    institutionName: string,
    countryRegion: string,
  ): Promise<any> => {
    const result = await getGPAConvertRule({
      variables: {
        institution: institutionName ?? '',
        countryRegion: countryRegion ?? '',
      },
    });
    return result.data?.convertGPARule;
  };

  const updateList = useCallback(async () => {
    await getMatchedRule(institutionName, countryRegion).then(result => {
      const { ruleName, ...others } = result?.defaultRule;
      const ruleList: GPAConvertRule[] = (result?.matchedRules ?? []).concat([
        {
          ruleName: NA,
          ...others,
        },
      ]);
      setMatchedRules(ruleList);
    });
    await formik.setFieldValue('selectRule', null);
  }, [institutionName, countryRegion]);

  useEffect(() => {
    if (institutionName ?? countryRegion) {
      updateList().catch(console.error);
    }
  }, [institutionName, countryRegion, updateList]);

  const convertGPAV2 = useConvertGPAV2();
  async function getConvertGPA(
    ruleId: string,
    originalGPA: string,
  ): Promise<number> {
    const result = await convertGPAV2({
      variables: {
        _id: ruleId,
        originalGPA: parseFloat(originalGPA),
        originalGPAOutOf: outOfGpa,
      },
    });

    let convertedGPA = result.data?.convertGPAV2?.convertedGPA ?? 0.0;
    convertedGPA = parseFloat(convertedGPA.toFixed(2));

    return convertedGPA;
  }

  const handleConvertGPA = async (): Promise<void> => {
    const convertedGPA = await getConvertGPA(
      formik.values.ruleId,
      formik.values.originalGPA as string,
    );
    if (convertedGPA !== undefined) {
      await formik.setFieldValue('convertedGPA', convertedGPA);
    }
  };
  const formik = useFormik<DialogV2ContentValue>({
    initialValues: {
      ruleId: convertGPARuleId ?? '',
      originalGPA: officialMark ?? 0,
      originalGPAOutOf: null,
      maxGPARange: 10000,
      minGPARange: -10000,
      convertedGPA: convertedMark ?? parseFloat((0).toFixed(2)),
    },
    validationSchema: object().shape({
      ruleId: string().required('Required'),
      convertedGPA: number().required('Required'),
      originalGPA: number()
        .max(ref('maxGPARange'), 'Out of range')
        .min(ref('minGPARange'), 'Out of range')
        .when('selectRule', ([selectRule], schema) => {
          const required: boolean = selectRule !== null;
          return required ? schema.required('Required') : schema.notRequired();
        }),
    }),
    onSubmit: handleConvertGPA,
  });

  const handleConvertAndApply = async (): Promise<void> => {
    await formik.submitForm();

    // Validate the form
    const errors = await formik.validateForm();
    if (Object.keys(errors).length !== 0 || !formik.isValid) {
      // If there are errors, return early and do not proceed
      return;
    }

    const newConvertedGPA = await getConvertGPA(
      formik.values.ruleId,
      formik.values.originalGPA as string,
    );

    if (newConvertedGPA !== undefined) {
      await formik.setFieldValue('convertedGPA', newConvertedGPA);
    }
    const { convertedGPA, ...other } = formik.values;
    if (onDialogApply)
      onDialogApply({ convertedGPA: Number(newConvertedGPA), ...other });
  };

  const handleRuleDisplay = (source?: any[]): SelectOption[] => {
    const newOptions =
      source?.map(item => {
        return {
          value: item._id,
          label: item.ruleName,
        };
      }) ?? [];

    // TODO quick fix, to be removed when the default rule is provided
    const ifValueConflict =
      newOptions.length === 2 && newOptions[0].value === newOptions[1].value;

    return ifValueConflict
      ? newOptions.filter(op => op.label !== NA)
      : newOptions;
  };

  const getRuleInformation = (
    ruleId: string,
  ): Partial<GPAConvertRule> | null => {
    if (ruleId === '') {
      return null;
    }
    const rule = matchedRules.find(r => r._id === ruleId);
    return {
      ...rule,
      note: rule?.note !== '' ? rule?.note : undefined,
    };
  };

  const displayRuleSelection = (options: SelectOption[]): React.ReactNode => {
    const currentRule = formik.values.ruleId;
    const { content, note } = getRuleInformation(currentRule) ?? {
      content: null,
      note: null,
    };

    const handleChange = async (event: any): Promise<void> => {
      const rule = getRuleInformation(event.target.value as string);
      await formik.setValues({
        ...formik.values,
        ruleId: event.target.value,
        maxGPARange: rule?.maxGPARange ?? 10000,
        minGPARange: rule?.minGPARange ?? -10000,
      });
    };

    return (
      <div>
        <Select
          className="w-full !pr-6"
          options={options}
          id="ruleId"
          name="ruleId"
          label={CONVERT_RULE}
          disabled={!enableToApply}
          required={true}
          InputLabelProps={{ shrink: true }}
          value={formik.values.ruleId}
          onChange={handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.ruleId && Boolean(formik.errors.ruleId)}
          helperText={formik.touched.ruleId && formik.errors.ruleId}
        />
        <div className="py-2">
          <h2 className={content != null ? '' : 'hidden'}>
            <span className="italic">{RULE_DETAIL}</span>
            {content}
          </h2>
          <h2 className={note != null ? '' : 'hidden'}>
            <span className="italic">{NOTE}</span>
            {note}
          </h2>
        </div>
      </div>
    );
  };

  const handleClose = (): void => {
    setOpenGPA(false);
    onDialogClose(formik.values);
  };

  return (
    <Dialog
      dialogTitle={dialogTitle}
      openDialog={openGPA}
      displayCloseButton={true}
      onDialogClose={handleClose}
      width="800px"
    >
      <ContainerStyle>
        {/* for dsrc member check */}
        {viewMode && (
          <div className="mx-8 px-1 mb-4 w-full">
            <h2 className="font-semibold mb-2">
              {RECORD}
              {':'}
            </h2>
            <div className="mx-4">
              <h2>
                {SELECTED_RULE}:&nbsp;{chosenRuleLabel}
              </h2>
              <h2>
                {ORIGINAL_GPA}:&nbsp;{formik.initialValues.originalGPA}
              </h2>
              <h2>
                {CONVERTED_GPA}:&nbsp;{formik.initialValues.convertedGPA}
              </h2>
            </div>
            <hr className="w-1/2 mt-4" />
          </div>
        )}
        <FormikProvider value={formik}>
          <form
            onSubmit={formik.handleSubmit}
            noValidate
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div>
              <div className="ml-8 py-2">
                {displayRuleSelection(handleRuleDisplay(matchedRules))}
              </div>
            </div>

            {/* Display area for GPA result */}
            <div
              className={`grid grid-cols-2 gap-4 px-8 py-4 mb-4 content-start`}
            >
              <TextField
                label={ORIGINAL_GPA}
                name="originalGPA"
                type="number"
                fullWidth
                disabled
                value={formik.values.originalGPA}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                required={true}
                error={
                  formik.touched.originalGPA &&
                  Boolean(formik.errors.originalGPA)
                }
                helperText={
                  formik.touched.originalGPA && formik.errors.originalGPA
                }
              />

              <TextField
                label={CONVERTED_GPA}
                name="convertedGPA"
                disabled={true}
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formik.values.convertedGPA}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required={true}
                error={
                  formik.touched.convertedGPA &&
                  Boolean(formik.errors.convertedGPA)
                }
                helperText={
                  formik.touched.convertedGPA && formik.errors.convertedGPA
                }
              />
            </div>
            <div className="button-area">
              {enableToApply && (
                <Button
                  variant="contained"
                  className="!ml-4"
                  onClick={handleConvertAndApply}
                >
                  {PAGE_TEXT.CONVERT}
                </Button>
              )}
              <Button variant="text" onClick={handleClose} className="!ml-4">
                {PAGE_TEXT.CANCEL}
              </Button>
            </div>
          </form>
        </FormikProvider>
      </ContainerStyle>
    </Dialog>
  );
};

const ContainerStyle = styled.div`
  margin: 1em 2em 1em;

  .button-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
`;
